/**
 * pi-collection-grid.js
 * Collection toolbar: filter drawer trigger, custom sort dropdown, view changer.
 * Works with Dawn facets.js — does not replace filtering/sorting logic.
 */

const PI_CPG_STORAGE_KEY = 'pi-collection-grid-columns';

class PiCollectionToolbar extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.defaultColumns = parseInt(this.dataset.defaultColumns, 10) || 4;
    this.defaultColumnsMobile = parseInt(this.dataset.defaultColumnsMobile, 10) || 2;
    this.viewChangerEnabled = this.dataset.viewChanger !== 'false';

    this.filterBtn = this.querySelector('[data-pi-filter-open]');
    this.sortRoot = this.querySelector('[data-pi-sort]');
    this.sortTrigger = this.querySelector('[data-pi-sort-trigger]');
    this.sortList = this.querySelector('[data-pi-sort-list]');
    this.sortValue = this.querySelector('[data-pi-sort-value]');
    this.viewBtnsMobile = this.querySelectorAll('[data-pi-view-scope="mobile"]');
    this.viewBtnsDesktop = this.querySelectorAll('[data-pi-view-scope="desktop"]');

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFilterDrawerChange = this.onFilterDrawerChange.bind(this);
    this.onGridUpdated = this.onGridUpdated.bind(this);
    this.onResize = this.onResize.bind(this);

    this.bindFilter();
    this.bindSort();
    this.bindViewChanger();
    this.syncSortFromNative();
    this.updateFilterCount();
    this.applyViewPreference();

    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onResize);
    this.observeFacets();
    this.observeProductGrid();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);
    this.unbindDocumentOverlayClose();
    if (this.onFilterBackdropClick && this._filterBackdropEl) {
      this._filterBackdropEl.removeEventListener('click', this.onFilterBackdropClick);
    }
    if (this.facetsObserver) this.facetsObserver.disconnect();
    if (this.gridObserver) this.gridObserver.disconnect();
  }

  get piCpgRoot() {
    return this.closest('.pi-cpg');
  }

  get productGrid() {
    return document.getElementById('product-grid');
  }

  get nativeSortSelect() {
    return (
      document.querySelector('#FacetSortDrawerForm #SortBy') ||
      document.querySelector('#FacetSortForm #SortBy') ||
      document.querySelector('facet-filters-form form .facet-filters__sort')
    );
  }

  get filterMenuDrawer() {
    return document.querySelector('#main-collection-filters menu-drawer.mobile-facets__wrapper');
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getStorageKey() {
    return PI_CPG_STORAGE_KEY + '-' + this.sectionId + '-' + (this.isMobileViewport() ? 'mobile' : 'desktop');
  }

  bindFilter() {
    if (!this.filterBtn) return;

    this.filterBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this.openFilterDrawer();
    });

    const details = this.filterMenuDrawer?.querySelector('details.mobile-facets__disclosure');
    if (details) {
      details.addEventListener('toggle', this.onFilterDrawerChange);
    }

    this.bindFilterBackdrop();
  }

  bindFilterBackdrop() {
    const menuDrawer = this.filterMenuDrawer;
    const mobileFacets = menuDrawer?.querySelector('.mobile-facets');
    const inner = menuDrawer?.querySelector('.mobile-facets__inner');
    if (!mobileFacets) return;

    if (this.onFilterBackdropClick && this._filterBackdropEl) {
      this._filterBackdropEl.removeEventListener('click', this.onFilterBackdropClick);
    }

    this._filterBackdropEl = mobileFacets;

    this.onFilterBackdropClick = (event) => {
      const details = menuDrawer.querySelector('details.mobile-facets__disclosure');
      if (!details?.classList.contains('menu-opening')) return;
      if (inner && inner.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeFilterDrawer();
    };

    mobileFacets.addEventListener('click', this.onFilterBackdropClick);
  }

  closeFilterDrawer() {
    const menuDrawer = this.filterMenuDrawer;
    if (!menuDrawer) return;

    const details = menuDrawer.querySelector('details.mobile-facets__disclosure');
    const summary = menuDrawer.querySelector('summary.mobile-facets__open-wrapper');
    if (!details || !summary || !details.hasAttribute('open')) return;

    if (typeof menuDrawer.closeMenuDrawer === 'function') {
      menuDrawer.closeMenuDrawer(new Event('click'), summary);
      return;
    }

    summary.click();
  }

  openFilterDrawer() {
    const menuDrawer = this.filterMenuDrawer;
    if (!menuDrawer) return;

    const details = menuDrawer.querySelector('details.mobile-facets__disclosure');
    const summary = menuDrawer.querySelector('summary.mobile-facets__open-wrapper');
    if (!details || !summary) return;

    if (details.hasAttribute('open') && details.classList.contains('menu-opening')) return;

    if (typeof menuDrawer.openMenuDrawer === 'function') {
      if (!details.hasAttribute('open')) {
        details.setAttribute('open', '');
      }
      menuDrawer.openMenuDrawer(summary);
      this.bindDocumentOverlayClose();
      return;
    }

    summary.click();
  }

  onFilterDrawerChange(event) {
    const open = event.target && event.target.open;
    if (this.filterBtn) {
      this.filterBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (open) {
      this.bindDocumentOverlayClose();
    } else {
      this.unbindDocumentOverlayClose();
    }
  }

  bindDocumentOverlayClose() {
    if (this._overlayCloseHandler) return;

    this._overlayCloseHandler = (event) => {
      const menuDrawer = this.filterMenuDrawer;
      const details = menuDrawer?.querySelector('details.mobile-facets__disclosure');
      const inner = menuDrawer?.querySelector('.mobile-facets__inner');
      if (!details?.classList.contains('menu-opening')) return;
      if (inner && inner.contains(event.target)) return;
      if (this.filterBtn && this.filterBtn.contains(event.target)) return;
      this.closeFilterDrawer();
    };

    setTimeout(() => {
      document.addEventListener('click', this._overlayCloseHandler, true);
    }, 0);
  }

  unbindDocumentOverlayClose() {
    if (!this._overlayCloseHandler) return;
    document.removeEventListener('click', this._overlayCloseHandler, true);
    this._overlayCloseHandler = null;
  }

  bindSort() {
    if (!this.sortRoot || !this.sortTrigger || !this.sortList) return;

    this.sortTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = this.sortRoot.classList.contains('is-open');
      this.setSortOpen(!isOpen);
    });

    this.sortList.addEventListener('click', (event) => {
      const option = event.target.closest('.pi-cpg__sort-option');
      if (!option) return;

      this.selectSort(option.dataset.value, option.textContent.trim());
      this.setSortOpen(false);
    });
  }

  setSortOpen(open) {
    this.sortRoot.classList.toggle('is-open', open);
    this.sortTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.sortList.hidden = !open;
  }

  selectSort(value, label) {
    const select = this.nativeSortSelect;
    if (!select) return;

    select.value = value;
    select.dispatchEvent(new Event('input', { bubbles: true }));

    if (label && this.sortValue) {
      this.sortValue.textContent = label;
    }

    this.sortList.querySelectorAll('.pi-cpg__sort-option').forEach((btn) => {
      const active = btn.dataset.value === value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  syncSortFromNative() {
    const select = this.nativeSortSelect;
    if (!select || !this.sortList) return;

    const activeOption = select.options[select.selectedIndex];
    const label = activeOption ? activeOption.textContent.trim() : '';

    if (label && this.sortValue) {
      this.sortValue.textContent = label;
    }

    this.rebuildSortOptions(select);
  }

  rebuildSortOptions(select) {
    if (!this.sortList) return;

    const currentValue = select.value;
    this.sortList.innerHTML = '';

    Array.from(select.options).forEach((option) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'none');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pi-cpg__sort-option';
      btn.setAttribute('role', 'option');
      btn.dataset.value = option.value;
      btn.textContent = option.textContent.trim();

      if (option.value === currentValue) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.setAttribute('aria-selected', 'false');
      }

      li.appendChild(btn);
      this.sortList.appendChild(li);
    });
  }

  bindViewChanger() {
    if (!this.viewChangerEnabled) return;

    [...this.viewBtnsMobile, ...this.viewBtnsDesktop].forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setView(parseInt(btn.dataset.piView, 10), true);
      });
    });
  }

  getStoredView() {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) return parseInt(stored, 10);
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  setStoredView(cols) {
    try {
      localStorage.setItem(this.getStorageKey(), String(cols));
    } catch (e) {
      /* ignore */
    }
  }

  getActiveViewButtons() {
    return this.isMobileViewport() ? this.viewBtnsMobile : this.viewBtnsDesktop;
  }

  setView(cols, persist) {
    const root = this.piCpgRoot;
    const grid = this.productGrid;
    if (!root || !grid) return;

    const isMobile = this.isMobileViewport();
    let viewCols;

    root.classList.remove('pi-cpg--view-1', 'pi-cpg--view-2', 'pi-cpg--view-3', 'pi-cpg--view-4');

    if (isMobile) {
      viewCols = cols === 1 ? 1 : 2;
      grid.classList.remove('grid--1-col-tablet-down', 'grid--2-col-tablet-down');
      grid.classList.add(viewCols === 1 ? 'grid--1-col-tablet-down' : 'grid--2-col-tablet-down');
    } else {
      viewCols = Math.min(Math.max(cols, 2), 4);
      grid.classList.remove(
        'grid--2-col-desktop',
        'grid--3-col-desktop',
        'grid--4-col-desktop',
        'grid--5-col-desktop',
        'grid--6-col-desktop'
      );
      grid.classList.add('grid--' + viewCols + '-col-desktop');
    }

    root.classList.add('pi-cpg--view-' + viewCols);

    this.getActiveViewButtons().forEach((btn) => {
      const active = parseInt(btn.dataset.piView, 10) === viewCols;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (persist) {
      this.setStoredView(viewCols);
    }
  }

  applyViewPreference() {
    const isMobile = this.isMobileViewport();

    if (!this.viewChangerEnabled) {
      this.setView(isMobile ? this.defaultColumnsMobile : this.defaultColumns, false);
      return;
    }

    const stored = this.getStoredView();
    let cols;

    if (isMobile) {
      cols = stored === 1 || stored === 2 ? stored : this.defaultColumnsMobile;
    } else {
      cols = stored >= 2 && stored <= 4 ? stored : this.defaultColumns;
    }

    this.setView(cols, false);
  }

  onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => this.applyViewPreference(), 150);
  }

  onDocumentClick(event) {
    if (this.sortRoot && !this.sortRoot.contains(event.target)) {
      this.setSortOpen(false);
    }
  }

  observeFacets() {
    const facetsRoot = document.querySelector('#main-collection-filters');
    if (!facetsRoot) return;

    this.facetsObserver = new MutationObserver(() => {
      this.syncSortFromNative();
      this.updateFilterCount();
      this.bindFilterBackdrop();
    });

    this.facetsObserver.observe(facetsRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  observeProductGrid() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    this.gridObserver = new MutationObserver((mutations) => {
      const gridReplaced = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          [...mutation.addedNodes].some(
            (node) =>
              node.nodeType === 1 &&
              ((node.id && node.id === 'product-grid') ||
                node.querySelector?.('#product-grid'))
          )
      );

      if (gridReplaced) {
        this.onGridUpdated();
      }
    });

    this.gridObserver.observe(container, { childList: true, subtree: true });
  }

  onGridUpdated() {
    this.applyViewPreference();
    this.updateFilterCount();
    PiCollectionInfiniteScroll.initAll();
  }

  updateFilterCount() {
    if (!this.filterBtn) return;

    const pills = document.querySelectorAll(
      '#main-collection-filters .active-facets-desktop facet-remove:not(.active-facets__button-wrapper)'
    );
    const count = pills.length;

    let badge = this.filterBtn.querySelector('.pi-cpg__filter-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'pi-cpg__filter-count';
        this.filterBtn.appendChild(badge);
      }
      badge.textContent = count;
      badge.setAttribute('aria-label', count + ' active filters');
    } else if (badge) {
      badge.remove();
    }
  }
}

if (!customElements.get('pi-collection-toolbar')) {
  customElements.define('pi-collection-toolbar', PiCollectionToolbar);
}

/**
 * Infinite scroll for collection product grid.
 * Uses scroll + IntersectionObserver and Dawn section rendering API.
 */
class PiCollectionInfiniteScroll {
  constructor(root) {
    this.root = root;
    this.spinner = root.querySelector('.pi-cpg__infinite-spinner');
    this.loadingWrap = root.querySelector('.pi-cpg__infinite-loading');
    this.isLoading = false;
    this.abortController = null;
    this.sectionId = root.dataset.sectionId || document.getElementById('product-grid')?.dataset.id;

    this.onScroll = this.onScroll.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '400px 0px' });
    this.observer.observe(this.root);

    this.checkShouldLoad();
  }

  static initAll() {
    document.querySelectorAll('[data-pi-infinite-scroll]').forEach((root) => {
      if (root._piInfiniteScroll) {
        root._piInfiniteScroll.destroy();
      }
      root._piInfiniteScroll = new PiCollectionInfiniteScroll(root);
    });
  }

  static observeGridContainer() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.dataset.piInfiniteObserved) return;

    container.dataset.piInfiniteObserved = 'true';
    new MutationObserver(() => {
      window.clearTimeout(PiCollectionInfiniteScroll._reinitTimer);
      PiCollectionInfiniteScroll._reinitTimer = window.setTimeout(() => {
        PiCollectionInfiniteScroll.initAll();
      }, 50);
    }).observe(container, { childList: true });
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    if (this.abortController) this.abortController.abort();
    delete this.root._piInfiniteScroll;
  }

  get productGrid() {
    return document.getElementById('product-grid');
  }

  get hasMore() {
    const nextPage = parseInt(this.root.dataset.nextPage, 10);
    const totalPages = parseInt(this.root.dataset.totalPages, 10);
    return Number.isFinite(nextPage) && Number.isFinite(totalPages) && nextPage <= totalPages;
  }

  onScroll() {
    this.checkShouldLoad();
  }

  onIntersect(entries) {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.checkShouldLoad();
    }
  }

  checkShouldLoad() {
    if (!this.hasMore || this.isLoading || !this.productGrid) return;

    const rect = this.root.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 400) {
      this.loadNextPage();
    }
  }

  buildFetchUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(page));
    params.delete('section_id');
    return `${window.location.pathname}?section_id=${this.sectionId}&${params.toString()}`;
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.root.classList.toggle('is-loading', loading);
    if (this.loadingWrap) {
      this.loadingWrap.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
    if (this.spinner) {
      this.spinner.classList.toggle('hidden', !loading);
    }
  }

  parseGridItems(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const container = parsed.getElementById('ProductGridContainer');
    const newGrid = container?.querySelector('#product-grid') || parsed.getElementById('product-grid');
    if (!newGrid) return { items: [], nextScroll: null };

    return {
      items: Array.from(newGrid.children).filter((node) => node.classList?.contains('grid__item')),
      nextScroll: (container || parsed).querySelector('[data-pi-infinite-scroll]'),
    };
  }

  loadNextPage() {
    if (!this.hasMore || this.isLoading || !this.productGrid || !this.sectionId) return;

    const nextPage = parseInt(this.root.dataset.nextPage, 10);
    this.setLoading(true);
    this.abortController = new AbortController();

    fetch(this.buildFetchUrl(nextPage), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Infinite scroll request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const { items, nextScroll } = this.parseGridItems(html);
        if (items.length === 0) return;

        items.forEach((item) => {
          item.querySelectorAll('.scroll-trigger').forEach((el) => {
            el.classList.remove('scroll-trigger--cancel');
          });
          this.productGrid.appendChild(item);
        });

        if (nextScroll?.dataset.nextPage) {
          this.root.dataset.nextPage = nextScroll.dataset.nextPage;
          this.root.dataset.totalPages = nextScroll.dataset.totalPages;
        } else {
          this.root.remove();
          this.destroy();
        }

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.productGrid);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('PiCollectionInfiniteScroll:', error);
        }
      })
      .finally(() => {
        this.setLoading(false);
        this.abortController = null;
        if (this.root.isConnected) {
          this.checkShouldLoad();
        }
      });
  }
}

PiCollectionInfiniteScroll.observeGridContainer();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => PiCollectionInfiniteScroll.initAll());
} else {
  PiCollectionInfiniteScroll.initAll();
}
